import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { Spinner } from 'components/spinner/Spinner';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { SubscribeApi } from 'api/subscribeApi';
import { useDispatch } from 'react-redux';
import { setQuotesId } from 'reducers/misc';
import Clear_promo_Code_Icon from '../../../images/Icon_Cross_05_White.png';
import { PricingSummaryDetailsApi } from '../../../components/pricingSummaryDetails/pricingSummaryDetailsApi';
import { ERROR_MESSAGE, INVALID_PROMO_CODE_MESSAGE } from 'appConstants';
import { getWithParams } from 'helpers/endpointCalls';
import { endpoints } from 'endpoints.config';
import { canApplyQuotePromoCode } from 'subscriptionOptions/quotePromoCodes';
import { quoteAnswersKey, quoteCountry } from '../quoteMint';
import { quotePackagesFromQuote } from 'pages/subscribe/quoteOptions';
import {
  applyCodeToSlots,
  droppedBySlotChange,
  PromoCodeSlots,
  removeCodeFromSlots,
  slotsFromQuotePackages,
  subjectsForPackageIds,
} from 'subscriptionOptions/promoCodeSlots';

type MOStep5Props = {
	setCurrentStep: Dispatch<SetStateAction<number | string>>;
	membershipQuestions: any;
	setMembershipQuestions: Dispatch<any>;
	setMembershipResultList: Dispatch<any>;
};

export const MOStep5: React.FC<MOStep5Props> = ({
	setCurrentStep,
	membershipQuestions,
	setMembershipQuestions,
	setMembershipResultList,
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [initialDetails] = useState<any>(membershipQuestions);
	const [promoCode, setPromoCode] = useState({ value: '', error: '' });
	const [applyingPromoCode, setApplyingPromoCode] = useState<boolean>(false);
	const [removingPromoCode, setRemovingPromoCode] = useState<boolean>(false);

	/** The minted quote's packages, so a checked code resolves to the subjects it covers. */
	const [quotePackages, setQuotePackages] = useState<any[]>([]);
	const [mintingQuote, setMintingQuote] = useState<boolean>(false);
	const [mintError, setMintError] = useState<string>('');
	/** The answers the held packages were minted from, so an edited answer mints again. */
	const mintedAnswersKey = useRef<string>('');
	/** Bumped to mint again after a failure, since the answers themselves have not changed. */
	const [mintAttempt, setMintAttempt] = useState<number>(0);
	/** The code each subject holds; a new code evicts those on the subjects it covers. */
	const [promoCodeSlots, setPromoCodeSlots] = useState<PromoCodeSlots>({});
	/** Codes an apply displaced, so a replaced discount is explained rather than vanishing. */
	const [droppedPromoCodes, setDroppedPromoCodes] = useState<string[]>([]);

	const dispatch = useDispatch();

	const answersKey = quoteAnswersKey(membershipQuestions);

	/** Mints the quote a promo code is checked against; only a changed answer mints again. */
	useEffect(() => {
		if(!answersKey || answersKey === mintedAnswersKey.current) return;

		let cancelled = false;
		mintedAnswersKey.current = answersKey;
		setMintingQuote(true);
		setMintError('');

		const mintQuote = async () => {
			const saved = await saveQuoteWithCodes(
				membershipQuestions,
				membershipQuestions.promoCodes || []
			);

			if(cancelled) return;

			if(!saved.data || saved.error){
				// A failed mint leaves nothing to check a code against, so let the next change retry.
				mintedAnswersKey.current = '';
				setQuotePackages([]);
				setMintError((saved.error && saved.error.message) || ERROR_MESSAGE);
				setMintingQuote(false);
				return;
			}

			dispatch(setQuotesId(saved.data.id));

			// savequote does not name each package's subject, so the quote is read back for it.
			const details = await getWithParams(endpoints.public.subscription.getQuoteStatus, {
				Id: saved.data.id,
			});

			if(cancelled) return;

			if(details.response && !details.errors){
				const packages = quotePackagesFromQuote(details.response);

				setQuotePackages(packages);
				// The quote already priced its codes per package.
				setPromoCodeSlots(slotsFromQuotePackages(packages));
			} else {
				mintedAnswersKey.current = '';
				setQuotePackages([]);
				setMintError(ERROR_MESSAGE);
			}

			setMintingQuote(false);
		};

		mintQuote();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [answersKey, mintAttempt]);

	/** The distinct codes the slots hold; one per subject, so never more than savequote carries. */
	const codesFromSlots = (slots: PromoCodeSlots): string[] =>
		Object.keys(slots)
			.map((subjectsId: string) => slots[Number(subjectsId)])
			.reduce(
				(codes: string[], code: string) =>
					codes.some((held) => held.toLowerCase() === code.toLowerCase())
						? codes
						: [...codes, code],
				[]
			);

	const saveQuoteWithCodes = async (values: any, codes: string[]) =>
		SubscribeApi.saveQuote(
			values.selectedPackage,
			values.selectedSubject,
			quoteCountry(values),
			values.vatCode,
			values.pupilsOnRoll,
			codes,
		);

	/**
	 * Adds a code checkpromocode confirms is real. A code it accepts must still cover one of
	 * this quote's subjects to be worth anything here.
	 */
	const applyPromoCode = async (formik: any) => {
		const values: any = formik.values;
		const code = promoCode.value.trim();
		const held: string[] = values.promoCodes || [];

		if (!code || applyingPromoCode || removingPromoCode || mintingQuote) return;

		const guard = canApplyQuotePromoCode(code, held);
		if (!guard.allowed) {
			setPromoCode((prevState) => ({ ...prevState, error: guard.error }));
			return;
		}

		if (quotePackages.length === 0) {
			setPromoCode((prevState) => ({ ...prevState, error: mintError || ERROR_MESSAGE }));
			return;
		}

		// The mint and the save below read different sources, equal while this step edits no
		// quote answer. Checked, not assumed: a code stored against a quote it was never checked
		// against loses its discount silently.
		if (quoteAnswersKey(values) !== mintedAnswersKey.current) {
			setPromoCode((prevState) => ({ ...prevState, error: ERROR_MESSAGE }));
			setMintAttempt((attempt) => attempt + 1);
			return;
		}

		setApplyingPromoCode(true);
		formik.setErrors({});

		try {
			const checked = await SubscribeApi.checkPromoCode(code);

			if (checked.error || !checked.data) {
				setPromoCode((prevState) => ({
					...prevState,
					error: (checked.error && checked.error.message) || INVALID_PROMO_CODE_MESSAGE,
				}));
				return;
			}

			const coveredSubjects = subjectsForPackageIds(checked.data, quotePackages);

			// A code the server accepted but that covers none of this quote's subjects buys nothing.
			if (coveredSubjects.length === 0) {
				setPromoCode((prevState) => ({ ...prevState, error: INVALID_PROMO_CODE_MESSAGE }));
				return;
			}

			const nextSlots = applyCodeToSlots(promoCodeSlots, code, coveredSubjects);
			const codes = codesFromSlots(nextSlots);
			const saved = await saveQuoteWithCodes(values, codes);

			if (!saved.data || saved.error) {
				setPromoCode((prevState) => ({
					...prevState,
					error: (saved.error && saved.error.message) || ERROR_MESSAGE,
				}));
				return;
			}

			dispatch(setQuotesId(saved.data.id));
			setDroppedPromoCodes(droppedBySlotChange(promoCodeSlots, nextSlots));
			setPromoCodeSlots(nextSlots);
			formik.setFieldValue("promoCodes", codes);
			setPromoCode({ value: '', error: '' });
		} catch (e) {
			setPromoCode((prevState) => ({ ...prevState, error: ERROR_MESSAGE }));
		} finally {
			setApplyingPromoCode(false);
		}
	};

	/** Re-saves without the code, so the stored quote never outlives a code the user removed. */
	const removePromoCode = async (formik: any, code: string) => {
		const values: any = formik.values;

		if (applyingPromoCode || removingPromoCode || mintingQuote) return;

		const nextSlots = removeCodeFromSlots(promoCodeSlots, code);
		const remaining = codesFromSlots(nextSlots);

		setPromoCodeSlots(nextSlots);
		setDroppedPromoCodes([]);
		formik.setFieldValue("promoCodes", remaining);
		setPromoCode({ value: '', error: '' });
		setRemovingPromoCode(true);

		try {
			const result = await saveQuoteWithCodes(values, remaining);

			if (result.data && !result.error) {
				dispatch(setQuotesId(result.data.id));
			}
		} catch (e) {
			setPromoCode({ value: '', error: ERROR_MESSAGE });
		} finally {
			setRemovingPromoCode(false);
		}
	};

	const handleSubmit = async (values: any, helpers: any) => {
		setLoading(true);

		const result = await saveQuoteWithCodes(values, values.promoCodes || []);

		if(result.data && !result.error){
			dispatch(setQuotesId(result.data.id));

			let priceSummDetailsResult = await PricingSummaryDetailsApi.getPublicPricingSummaryDetails({
				quotesId: result.data.id,
				packagePromos: result.data.details && result.data.details.packagePromos,
			});

			values.pricingSummary = priceSummDetailsResult.data;

			setMembershipQuestions(values);
			setMembershipResultList(true);
			setLoading(false);
		} else {
			helpers.setErrors({ general: result.error.message });
			setLoading(false);
		}
	};

	return (
		<>
			<div className="MOSpeechBubbleQuestion">
				<div className="SpeechBubbleQ">
					<h1>Do you have a promo code?</h1>
				</div>
			</div>

			<Formik
				initialValues={initialDetails}
				onSubmit={handleSubmit}
				validateOnChange={false}
				validateOnBlur={false}
				validateOnMount={false}
				enableReinitialize={true}
			>
				{(formik) => {
					let values: any = formik.values;
					const held: string[] = values.promoCodes || [];

					return (
						<Form>
							<div className="MOQForm">
								{formik.errors.general && <p className="ErrorTxt">{(formik as any).errors.general}</p>}

								{/* Without packages every code would be refused, so say so. */}
								{mintError &&
									<p className="ErrorTxt">
										{mintError}{' '}
										<button
											className="ErrorTxtLink"
											type="button"
											onClick={() => setMintAttempt((attempt) => attempt + 1)}
										>
											Try again
										</button>
									</p>
								}

								<div className="FormBox PromoCodeField">
									<div className="FormField">
										{loading && <Spinner />}

										<div className="DiscoundContainer">
											<input
												className="Discount EditBox"
												type="text"
												placeholder="Enter promo code (optional)"
												value={promoCode.value}
												disabled={applyingPromoCode || removingPromoCode || mintingQuote}
												onChange={(e) =>
													setPromoCode({ value: e.target.value, error: '' })
												}
												onKeyDown={(e) => {
													if (e.key === 'Enter') {
														e.preventDefault();
														applyPromoCode(formik);
													}
												}}
											/>

											<button
												className="Btn BtnOrange"
												type="button"
												disabled={!promoCode.value.trim() || applyingPromoCode || removingPromoCode || mintingQuote}
												onClick={() => applyPromoCode(formik)}
											>
												{applyingPromoCode ? 'Applying...' : 'Apply'}
											</button>

											{promoCode.error && <FormGeneralError message={promoCode.error} />}
										</div>
									</div>
								</div>

								{held.length > 0 &&
									<div className="PromoCodeList">
										{held.map((code: string, index: number) => (
											<div className="PCItem" key={code + "_" + index}>
												<div className="PCName">{code}</div>
												<div
													className="PCDeleteIcon"
													onClick={() => removePromoCode(formik, code)}
												>
													<img src={Clear_promo_Code_Icon} alt="Delete promo code" />
												</div>
											</div>
										))}

										{/* checkpromocode confirms a code is real; what it is worth depends on the
										     packages, so the tiles that follow show what it actually saved. */}
										<p className="SmTxt">Your discount is shown with your quote.</p>

										{droppedPromoCodes.length > 0 &&
											<p className="SmTxt">
												{droppedPromoCodes.join(', ')}{' '}
												{droppedPromoCodes.length > 1 ? 'are' : 'is'} no longer applied.
											</p>
										}
									</div>
								}

								<div className="Btn BtnOrange SubmitBtn" onClick={formik.submitForm}>Get quote</div>
								<div className="Btn BtnWhite BackBtn" onClick={() => {
									setMembershipQuestions(values);
									setCurrentStep(4);
								}}>Back</div>
							</div>
						</Form>
					);
				}}
			</Formik>
		</>
	);
};
